import React, { useState } from 'react';
import { 
  Box, Typography, Paper, Grid, Button, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, Chip 
} from '@mui/material';
import { db } from '../../services/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';

const LapsedDonorReport = () => {
  const { userProfile } = useAuth();
  const churchId = userProfile?.churchId;

  const [lapsedDonors, setLapsedDonors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasRun, setHasRun] = useState(false);

  const runReport = async () => {
    setLoading(true);
    try {
      const today = new Date();
      // "Recent" = Last 90 Days
      const recentDate = new Date();
      recentDate.setDate(today.getDate() - 90);
      
      // "Past" = 1 Year Ago to 90 Days Ago
      const pastDate = new Date();
      pastDate.setDate(today.getDate() - 365);

      const txRef = collection(db, 'churches', churchId, 'financials', 'data', 'transactions');
      
      // 1. Fetch ALL transactions for the last year (roughly) to analyze
      // (Fetching strictly by date range in queries can be tricky if we need "NOT IN" logic,
      // so we fetch the broad set and filter in JS for flexibility)
      const q = query(
          txRef, 
          where('status', '==', 'posted'),
          where('date', '>=', pastDate.toISOString().split('T')[0])
      );
      const snap = await getDocs(q);

      const recentGivers = new Set();
      const pastGivers = new Map(); // ID -> { name, lastGiftDate }

      snap.docs.forEach(doc => {
          const d = doc.data();
          // Skip Opening Balances
          if (d.type === 'opening_balance') return;

          const donorId = d.donorId || d.donorName; // Fallback to name if ID missing
          if (!donorId) return;

          const txDate = new Date(d.date);

          if (txDate >= recentDate) {
              recentGivers.add(donorId);
          } else {
              // It's an older gift
              if (!pastGivers.has(donorId)) {
                  pastGivers.set(donorId, { 
                      name: d.donorName || "Unknown", 
                      lastGift: d.date 
                  });
              } else {
                  // Update last gift if this one is newer (but still old)
                  const current = pastGivers.get(donorId);
                  if (d.date > current.lastGift) {
                      current.lastGift = d.date;
                  }
              }
          }
      });

      // 2. Identify Lapsed: In Past Map BUT NOT in Recent Set
      const results = [];
      pastGivers.forEach((val, key) => {
          if (!recentGivers.has(key)) {
              results.push({ id: key, ...val });
          }
      });

      setLapsedDonors(results.sort((a,b) => b.lastGift.localeCompare(a.lastGift))); // Most recent lapsed first
      setHasRun(true);

    } catch (e) {
      alert("Error: " + e.message);
    }
    setLoading(false);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
            <Typography variant="h5">Lapsed Donor Report</Typography>
            <Typography variant="body2" color="textSecondary">
                Donors who gave in the last year but NOT in the last 90 days.
            </Typography>
        </Box>
        <Button variant="contained" onClick={runReport} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : "Scan Database"}
        </Button>
      </Box>

      {hasRun && (
          <Paper>
              <Table>
                  <TableHead sx={{ bgcolor: '#fff3e0' }}>
                      <TableRow>
                          <TableCell>Donor Name</TableCell>
                          <TableCell>Last Gift Date</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Action</TableCell>
                      </TableRow>
                  </TableHead>
                  <TableBody>
                      {lapsedDonors.map(d => (
                          <TableRow key={d.id} hover>
                              <TableCell sx={{ fontWeight: 'bold' }}>{d.name}</TableCell>
                              <TableCell>{d.lastGift}</TableCell>
                              <TableCell><Chip label="At Risk" color="warning" size="small" /></TableCell>
                              <TableCell align="right">
                                  <Button size="small" variant="outlined">Log Contact</Button>
                              </TableCell>
                          </TableRow>
                      ))}
                      {lapsedDonors.length === 0 && (
                          <TableRow><TableCell colSpan={4} align="center">Good news! No lapsed donors found.</TableCell></TableRow>
                      )}
                  </TableBody>
              </Table>
          </Paper>
      )}
    </Box>
  );
};

export default LapsedDonorReport;